import React, { Fragment, useState } from 'react';
import { NeedsAttentionItem } from '../needsAttention';
import '../../Home.scss';

interface NeedsAttentionRailProps {
  items: NeedsAttentionItem[];
  onSelect: (item: NeedsAttentionItem) => void;
  /** Highlights the item currently on screen (used by /monitor). */
  activeShipmentId?: string | null;
  /** Show at most this many items plus a "+N more" line (no scrolling). */
  maxVisible?: number;
  /** The real number needing attention when `items` is only a subset. */
  totalCount?: number;
  /** A footer line under the list (e.g. what later loops will show). */
  footer?: string;
  /** Header text; /monitor names its first group when nothing needs attention. */
  title?: string;
  /**
   * A heading over the first item of each of these groups after the first
   * (/monitor's loop: "Arriving soon", "On watch", "Picking up soon").
   */
  sectionLabels?: Partial<Record<NeedsAttentionItem['severity'], string>>;
}

const DEFAULT_SECTIONS: NeedsAttentionRailProps['sectionLabels'] = {
  arriving: 'Arriving soon',
};

const SEVERITY_LABELS: Record<NeedsAttentionItem['severity'], string> = {
  critical: 'Critical',
  at_risk: 'At risk',
  watch: 'Watch',
  arriving: 'Arriving',
  pickup: 'Pickup',
};

/**
 * Ranked triage rail: the shipments most likely to need intervention,
 * highest urgency first. Clicking an entry flies the map to the shipment
 * and opens its detail card.
 */
const NeedsAttentionRail: React.FC<NeedsAttentionRailProps> = ({
  items,
  onSelect,
  activeShipmentId = null,
  maxVisible,
  totalCount,
  footer,
  title = 'Needs attention',
  sectionLabels = DEFAULT_SECTIONS,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!items?.length) return null;

  return (
    <div className='needs-attention-rail'>
      <button
        type='button'
        className='needs-attention-rail-header'
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className='needs-attention-rail-title'>
          {title}
          <span className='needs-attention-rail-count'>
            {totalCount ?? items.length}
          </span>
        </span>
        <span className='needs-attention-rail-chevron'>
          {collapsed ? '▸' : '▾'}
        </span>
      </button>
      {!collapsed && (
        <ul className='needs-attention-rail-list'>
          {(maxVisible ? items.slice(0, maxVisible) : items).map(
            (item, index, shown) => (
              <Fragment key={item.shipmentId}>
                {sectionLabels?.[item.severity] &&
                  index > 0 &&
                  shown[index - 1].severity !== item.severity && (
                    <li className='needs-attention-rail-section'>
                      {sectionLabels[item.severity]}
                    </li>
                  )}
                <li>
                  <button
                    type='button'
                    className={`needs-attention-rail-item${
                      String(item.shipmentId) === String(activeShipmentId)
                        ? ' needs-attention-rail-item--active'
                        : ''
                    }`}
                    aria-current={
                      String(item.shipmentId) === String(activeShipmentId)
                        ? 'true'
                        : undefined
                    }
                    onClick={() => onSelect(item)}
                  >
                    <div className='needs-attention-rail-item-top'>
                      <span
                        className='needs-attention-rail-item-ref'
                        title={item.referenceNumber}
                      >
                        {item.referenceNumber}
                      </span>
                      <span
                        className={`needs-attention-severity needs-attention-severity-${item.severity}`}
                      >
                        {SEVERITY_LABELS[item.severity]}
                      </span>
                    </div>
                    {item.reasons.length > 0 && (
                      <div className='needs-attention-rail-item-reasons'>
                        {item.reasons.join(' · ')}
                      </div>
                    )}
                  </button>
                </li>
              </Fragment>
            )
          )}
        </ul>
      )}
      {!collapsed && maxVisible && items.length > maxVisible && (
        <div className='needs-attention-rail-more'>
          +{items.length - maxVisible} more
        </div>
      )}
      {!collapsed && footer && (
        <div className='needs-attention-rail-more'>{footer}</div>
      )}
    </div>
  );
};

export default NeedsAttentionRail;
